// Shared FAQ manager for Grace/Dawn's knowledge base (the "grace_faq" table).
// Used by both AdminDashboard.tsx (Grace & Dawn FAQ tab) and VoiceCommandCenter.tsx
// (Training/QA workflow) so the QA team can add/edit FAQ entries without
// leaving the Voice Center. Both Grace and Dawn read from this same table.
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { RefreshCw, Plus, Upload, MessageSquare, ExternalLink, Pencil, Trash2 } from "lucide-react";

const FAQ_CATEGORIES = ["Account & Access", "Referral Links", "Leads & Pipeline", "Appointments", "Commissions", "Documents", "Share360", "Training", "Dashboard", "Grace Assistant", "Common Errors", "Support", "Registration", "Fundable360", "Legacy360", "Media360", "Scale360", "Certifications", "General"];

export function GraceFaqAdminTab() {
  const utils = trpc.useUtils();
  const faqQuery = trpc.grace.listAll.useQuery();
  const createMutation = trpc.grace.create.useMutation({ onSuccess: () => { utils.grace.listAll.invalidate(); setForm(null); } });
  const updateMutation = trpc.grace.update.useMutation({ onSuccess: () => { utils.grace.listAll.invalidate(); setEditId(null); setForm(null); } });
  const deleteMutation = trpc.grace.delete.useMutation({ onSuccess: () => utils.grace.listAll.invalidate() });
  const kbRefreshMutation = trpc.grace.refreshKnowledgeBase.useMutation();
  const bulkImportMutation = trpc.grace.bulkImport.useMutation({
    onSuccess: (data) => {
      utils.grace.listAll.invalidate();
      setBulkText("");
      setShowBulk(false);
      toast.success(`Imported ${data.imported} entries${data.skipped ? `, skipped ${data.skipped} duplicates` : ""}`);
    },
    onError: (e) => toast.error(e.message),
  });

  type Audience = "grace" | "dawn" | "both";
  type FaqForm = { question: string; answer: string; category: string; link: string; audience: Audience; sortOrder: number; isActive: boolean };
  const emptyForm: FaqForm = { question: "", answer: "", category: "General", link: "", audience: "both", sortOrder: 0, isActive: true };
  const [form, setForm] = useState<FaqForm | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [filterCat, setFilterCat] = useState("all");
  const [filterAudience, setFilterAudience] = useState<"all" | Audience>("all");
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");

  function handleBulkImport() {
    let entries: { question: string; answer: string; category?: string; audience?: Audience }[] = [];
    try {
      entries = JSON.parse(bulkText);
      if (!Array.isArray(entries)) throw new Error("Must be a JSON array");
    } catch {
      toast.error("Invalid JSON — paste a valid array of { question, answer, category?, audience? } objects");
      return;
    }
    bulkImportMutation.mutate({ entries, category: "Imported" });
  }

  const faqs = faqQuery.data ?? [];
  const filtered = faqs
    .filter((f: any) => filterCat === "all" || f.category === filterCat)
    .filter((f: any) => filterAudience === "all" || (f.audience ?? "both") === filterAudience);

  function startCreate() { setEditId(null); setForm({ ...emptyForm }); }
  function startEdit(faq: any) {
    setEditId(faq.id);
    setForm({ question: faq.question, answer: faq.answer, category: faq.category ?? "General", link: faq.link ?? "", audience: faq.audience ?? "both", sortOrder: faq.sortOrder ?? 0, isActive: faq.isActive ?? true });
  }
  function cancelForm() { setForm(null); setEditId(null); }

  function handleSave() {
    if (!form) return;
    if (editId !== null) {
      updateMutation.mutate({ id: editId, ...form });
    } else {
      createMutation.mutate(form);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Grace &amp; Dawn — FAQ Manager</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            Both assistants search these entries before calling the LLM. Set <span className="font-semibold text-gray-500">Audience</span> to control which bot each entry is eligible for — "Both" (the default) is visible to both.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => kbRefreshMutation.mutate()}
            disabled={kbRefreshMutation.isPending}
            title="Force-refresh the Knowledge Base cache so Dawn uses the latest published articles"
            className="flex items-center gap-1.5 px-3 py-2 bg-[#1A6FD4] hover:bg-[#1558b0] disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${kbRefreshMutation.isPending ? "animate-spin" : ""}`} />
            {kbRefreshMutation.isPending ? "Syncing…" : kbRefreshMutation.data ? `Synced (${kbRefreshMutation.data.articleCount} articles)` : "Sync KB"}
          </button>
          <button
            onClick={startCreate}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#84ab4a] hover:bg-[#6e9038] text-white text-sm font-semibold rounded-xl transition-colors"
          >
            <Plus className="w-4 h-4" /> Add FAQ
          </button>
          <button
            onClick={() => setShowBulk(v => !v)}
            className="flex items-center gap-1.5 px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-semibold rounded-xl transition-colors"
          >
            <Upload className="w-4 h-4" /> Bulk Import
          </button>
        </div>
      </div>

      {/* Bulk Import Panel */}
      {showBulk && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-sm text-gray-600 font-medium">
            Paste a JSON array of FAQ entries. Each item must have <code className="bg-gray-200 px-1 rounded">question</code> and <code className="bg-gray-200 px-1 rounded">answer</code> fields (optional: <code className="bg-gray-200 px-1 rounded">category</code>). Duplicates are skipped.
          </p>
          <pre className="text-xs text-gray-500 bg-white border border-gray-200 rounded-lg p-2">{`[
  { "question": "What is Share360?", "answer": "Share360 is a zero-cost...", "category": "Share360" },
  { "question": "What is Fundable360?", "answer": "Fundable360 helps...", "category": "Fundable360" }
]`}</pre>
          <textarea
            value={bulkText}
            onChange={e => setBulkText(e.target.value)}
            rows={8}
            placeholder='[{ "question": "...", "answer": "...", "category": "..." }]'
            className="w-full text-sm font-mono border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#84ab4a] resize-y"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkImport}
              disabled={bulkImportMutation.isPending || !bulkText.trim()}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#84ab4a] hover:bg-[#6e9038] disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              {bulkImportMutation.isPending ? "Importing…" : "Import"}
            </button>
            <button
              onClick={() => { setShowBulk(false); setBulkText(""); }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
            {bulkImportMutation.data && (
              <span className="text-sm text-gray-500">
                Last import: {bulkImportMutation.data.imported} added, {bulkImportMutation.data.skipped} skipped
              </span>
            )}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap gap-2">
        {["all", ...FAQ_CATEGORIES].map(cat => (
          <button
            key={cat}
            onClick={() => setFilterCat(cat)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filterCat === cat ? "bg-[#84ab4a] text-white border-[#84ab4a]" : "border-gray-200 text-gray-500 hover:border-[#84ab4a] hover:text-[#84ab4a]"}`}
          >
            {cat === "all" ? `All (${faqs.length})` : cat}
          </button>
        ))}
      </div>

      {/* Audience filter */}
      <div className="flex flex-wrap gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 self-center mr-1">Audience</span>
        {(["all", "grace", "dawn", "both"] as const).map(aud => (
          <button
            key={aud}
            onClick={() => setFilterAudience(aud)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filterAudience === aud ? "bg-[#1A6FD4] text-white border-[#1A6FD4]" : "border-gray-200 text-gray-500 hover:border-[#1A6FD4] hover:text-[#1A6FD4]"}`}
          >
            {aud === "all" ? "All" : aud === "grace" ? "Grace only" : aud === "dawn" ? "Dawn only" : "Both"}
          </button>
        ))}
      </div>

      {/* Add / Edit form */}
      {form && (
        <div className="bg-white border border-[#84ab4a]/30 rounded-2xl shadow-sm p-5 space-y-4">
          <div className="text-sm font-semibold text-gray-700">{editId !== null ? "Edit FAQ" : "New FAQ"}</div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Question</label>
            <input
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
              placeholder="e.g. How do I register someone to my team?"
              value={form.question}
              onChange={e => setForm(f => f ? { ...f, question: e.target.value } : f)}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Answer</label>
            <textarea
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a] resize-y"
              rows={4}
              placeholder="Write a clear, concise answer Dawn will use verbatim..."
              value={form.answer}
              onChange={e => setForm(f => f ? { ...f, answer: e.target.value } : f)}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Link (optional)</label>
            <input
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
              placeholder="https://... — shown as a 'Learn more' button in Dawn"
              value={form.link}
              onChange={e => setForm(f => f ? { ...f, link: e.target.value } : f)}
            />
          </div>
          <div className="flex gap-3 flex-wrap">
            <div className="flex-1 min-w-[140px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Category</label>
              <select
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
                value={form.category}
                onChange={e => setForm(f => f ? { ...f, category: e.target.value } : f)}
              >
                {FAQ_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="min-w-[140px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Audience</label>
              <select
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
                value={form.audience}
                onChange={e => setForm(f => f ? { ...f, audience: e.target.value as Audience } : f)}
              >
                <option value="both">Both (Grace &amp; Dawn)</option>
                <option value="grace">Grace only</option>
                <option value="dawn">Dawn only</option>
              </select>
            </div>
            <div className="w-24">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Sort</label>
              <input
                type="number"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
                value={form.sortOrder}
                onChange={e => setForm(f => f ? { ...f, sortOrder: parseInt(e.target.value) || 0 } : f)}
              />
            </div>
            <div className="flex items-end pb-0.5">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <div
                  onClick={() => setForm(f => f ? { ...f, isActive: !f.isActive } : f)}
                  className={`w-9 h-5 rounded-full transition-colors flex items-center px-0.5 ${form.isActive ? "bg-[#84ab4a]" : "bg-gray-200"}`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${form.isActive ? "translate-x-4" : "translate-x-0"}`} />
                </div>
                <span className="text-xs text-gray-600">{form.isActive ? "Active" : "Inactive"}</span>
              </label>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSave}
              disabled={isSaving || !form.question.trim() || !form.answer.trim()}
              className="px-4 py-2 bg-[#84ab4a] hover:bg-[#6e9038] text-white text-sm font-semibold rounded-xl disabled:opacity-40 transition-colors"
            >
              {isSaving ? "Saving…" : editId !== null ? "Update" : "Create"}
            </button>
            <button onClick={cancelForm} className="px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* FAQ list */}
      {faqQuery.isLoading ? (
        <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-200" />
          <p className="text-sm text-gray-400">No FAQ entries yet. Add one to help Dawn answer associate questions.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((faq: any) => (
            <div key={faq.id} className={`bg-white border rounded-2xl px-5 py-4 flex items-start gap-4 ${!faq.isActive ? "opacity-50" : "border-gray-100"}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {faq.category && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#84ab4a]/10 text-[#84ab4a] uppercase tracking-wide">{faq.category}</span>
                  )}
                  {faq.audience && faq.audience !== "both" && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#1A6FD4]/10 text-[#1A6FD4] uppercase tracking-wide">
                      {faq.audience === "grace" ? "Grace only" : "Dawn only"}
                    </span>
                  )}
                  {!faq.isActive && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">INACTIVE</span>}
                </div>
                <div className="text-sm font-semibold text-gray-800 mb-1">{faq.question}</div>
                <div className="text-sm text-gray-500 whitespace-pre-wrap leading-relaxed">{faq.answer}</div>
                {faq.link && (
                  <a href={faq.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#84ab4a] hover:underline mt-1.5">
                    <ExternalLink className="w-3 h-3" /> {faq.link}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => startEdit(faq)}
                  className="p-1.5 text-gray-400 hover:text-[#84ab4a] hover:bg-[#84ab4a]/8 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { if (confirm("Delete this FAQ?")) deleteMutation.mutate({ id: faq.id }); }}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
